/**
 * Page-level facet aggregation for Storefront Hunter.
 * Counts apply only to the currently loaded product list — not seller-wide totals.
 */

const PAGE_FACET_MAX = 10;
const CLASS_TYPE_VALUES = new Set(["variant", "regular"]);

/**
 * Normalize retail category from product fields present in the API contract.
 * Never infers from title or classType.
 * @param {Record<string, unknown>|null|undefined} product
 * @returns {string|null}
 */
export function normalizeCategory(product) {
  if (!product || typeof product !== "object") return null;

  const pathRaw = product.category_path ?? product.categoryPath;
  if (Array.isArray(pathRaw)) {
    const joined = pathRaw
      .map((part) => (part == null ? "" : String(part).trim()))
      .filter(Boolean)
      .join(" > ");
    if (joined) return joined;
  } else if (typeof pathRaw === "string" && pathRaw.trim()) {
    return pathRaw.trim();
  }

  const raw =
    product.category ?? product.category_name ?? product.categoryName ?? null;

  if (raw == null) return null;
  if (typeof raw !== "string") return null;
  const category = raw.trim();
  if (!category) return null;
  if (CLASS_TYPE_VALUES.has(category.toLowerCase())) return null;
  if (category.toUpperCase() === "VARIANT" || category.toUpperCase() === "REGULAR") {
    return null;
  }
  return category;
}

function normalizeFacetLabel(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  return s;
}

/**
 * Aggregate a product string field into ranked page facets.
 * @param {Array} products
 * @param {'brand'|'category'} field
 * @param {number} [max]
 * @returns {{ name: string, count: number }[]}
 */
export function aggregatePageFacet(products, field, max = PAGE_FACET_MAX) {
  const counts = new Map(); // lowerKey -> { name, count }
  for (const p of products || []) {
    const label =
      field === "category" ? normalizeCategory(p) : normalizeFacetLabel(p?.[field]);
    if (!label) continue;
    const key = label.toLowerCase();
    const existing = counts.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      counts.set(key, { name: label, count: 1 });
    }
  }
  return Array.from(counts.values())
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    })
    .slice(0, Math.max(0, max));
}

/**
 * @param {Array} products — currently loaded result-page products
 * @returns {{ brands: {name:string,count:number}[], categories: {name:string,count:number}[] }}
 */
export function buildPageFacets(products, { max = PAGE_FACET_MAX } = {}) {
  return {
    brands: aggregatePageFacet(products, "brand", max),
    categories: aggregatePageFacet(products, "category", max),
  };
}

function countWith(products, field) {
  let n = 0;
  for (const p of products || []) {
    const label = field === "category" ? normalizeCategory(p) : normalizeFacetLabel(p?.[field]);
    if (label) n += 1;
  }
  return n;
}

/**
 * Brand / category ranking over every product loaded so far for the seller
 * (all visited pages), with an explicit scope. Counts are never presented as
 * store-wide unless every catalog product has been loaded.
 * @param {Array} loadedProducts — unique products across loaded pages
 * @param {{ catalogTotal?: number|null, pagesLoaded?: number, catalogFacets?: object|null, max?: number }} opts
 */
export function buildLoadedCatalogFacets(
  loadedProducts,
  { catalogTotal = null, pagesLoaded = 1, catalogFacets = null, max = PAGE_FACET_MAX } = {}
) {
  const products = loadedProducts || [];
  const loadedCount = products.length;
  const total = Number.isFinite(Number(catalogTotal)) && catalogTotal != null ? Number(catalogTotal) : null;
  const complete = total != null && loadedCount > 0 && loadedCount >= total;
  let scopeLabel;
  if (complete) scopeLabel = "Entire Catalog";
  else if (pagesLoaded <= 1) scopeLabel = "This Page";
  else scopeLabel = "Loaded Products";

  const storeWideBrandCount =
    catalogFacets && Number.isFinite(Number(catalogFacets.brand_count))
      ? Number(catalogFacets.brand_count)
      : null;

  return {
    brands: aggregatePageFacet(products, "brand", max),
    categories: aggregatePageFacet(products, "category", max),
    scope: {
      label: scopeLabel,
      complete,
      loadedCount,
      pagesLoaded,
      catalogTotal: total,
      brandKnownCount: countWith(products, "brand"),
      categoryKnownCount: countWith(products, "category"),
    },
    storeWideBrands:
      storeWideBrandCount != null
        ? {
            count: storeWideBrandCount,
            lowerBound: catalogFacets.brand_count_is_lower_bound === true,
          }
        : null,
  };
}

/** "Based on 80 of 1,962 products" style scope note; null when complete. */
export function facetScopeNote(scope, knownCount) {
  if (!scope || scope.complete || !scope.loadedCount) return null;
  const of =
    scope.catalogTotal != null
      ? ` of ${Number(scope.catalogTotal).toLocaleString()}`
      : "";
  const known =
    knownCount != null && knownCount < scope.loadedCount
      ? ` (${knownCount.toLocaleString()} with data)`
      : "";
  return `Based on ${scope.loadedCount.toLocaleString()}${of} products${known}`;
}

export { PAGE_FACET_MAX };
